import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Inject,
  Input,
  Optional
} from '@angular/core';
import { DwLookupInfo } from '@devwareapps/devware-cap';
import { ActivatedRoute } from '@angular/router';
import {
  DwEventService,
  DwInputConverter,
  DwLookupParameterUtilService,
  DwMetaDataFormStateService,
  DwMetaDataRoutingService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSecurityUserService,
  DwSelectLookupComponent,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken,
  booleanConverter
} from '@devwareapps/devware-cap';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

interface AviatorLookupRenderedItem {
  isClear: boolean;
  display: string;
  isSelected: boolean;
  key: string | number;
  lookupItem: DwLookupInfo;
  optionId: string;
  value: any;
}

@Component({
  selector: 'app-aviator-lookup-dropdown',
  templateUrl: './aviator-lookup-dropdown.component.html',
  styleUrls: ['./aviator-lookup-dropdown.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AviatorLookupDropdownComponent),
    multi: true
  }],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AviatorLookupDropdownComponent extends DwSelectLookupComponent {
  private static openDropdownInstance: AviatorLookupDropdownComponent | null = null;
  private readonly clearSelectionKey = '__clear-selection__';

  @Input() placeholder = '';

  @Input() clearOptionText = 'Clear selection';

  @DwInputConverter(booleanConverter)
  @Input() showFilter = true;

  @Input() filterPlaceholder = 'Type to filter';

  @Input() noRowMessage = 'No options available';

  @DwInputConverter(booleanConverter)
  @Input() invalid = false;

  @Input() inputId: string;

  @Input() name: string;

  @Input() ariaDescribedBy: string;

  isOpen = false;
  activeOptionIndex = -1;
  filterText = '';
  activeDescendantId: string | null = null;
  panelId: string;
  renderedLookupItems: AviatorLookupRenderedItem[] = [];

  constructor(
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwEventService: DwEventService,
    dwSecurityService: DwSecurityUserService,
    cdr: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
    dwMetaDataRoutingService: DwMetaDataRoutingService,
    dwLookupParameterUtilService: DwLookupParameterUtilService,
    route: ActivatedRoute,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Optional() dwMetaDataFormStateService: DwMetaDataFormStateService
  ) {
    super(
      dwMetaDataService,
      dwEventService,
      dwSecurityService,
      cdr,
      dwMetaDataRoutingService,
      dwLookupParameterUtilService,
      route,
      uiMetaDataConfig,
      dwMetaDataFormStateService
    );
  }

  get displayBlankOptionText(): string {
    return this.blankOptionText || this.placeholder || '';
  }

  get selectedDisplayText(): string {
    const selectedItem = this.getLookupItemByValue(this.translatedValue);

    if (selectedItem) {
      return selectedItem.Display;
    }

    return '';
  }

  getLookupItemDisplay(lookupItem: DwLookupInfo): string {
    if (!lookupItem) {
      return '';
    }

    return lookupItem.Display
      || lookupItem['Description']
      || lookupItem['LookupValue']
      || lookupItem['Label']
      || lookupItem['StateProvinceShortName']
      || lookupItem['Name']
      || lookupItem[this.valueAttrName]
      || '';
  }

  toggleDropdown(): void {
    if (this.disabled) {
      return;
    }

    if (this.isOpen) {
      this.closeDropdown();
      return;
    }

    this.openDropdown();
  }

  selectLookupItem(lookupItem: DwLookupInfo): void {
    this.setTranslatedValue(lookupItem ? lookupItem[this.valueAttrName] : null);
    this.closeDropdown();
  }

  selectRenderedLookupItem(item: AviatorLookupRenderedItem): void {
    if (!item) {
      return;
    }

    if (item.isClear) {
      this.setTranslatedValue(null);
      this.closeDropdown();
      return;
    }

    this.selectLookupItem(item.lookupItem);
  }

  onOptionMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onOptionClick(item: AviatorLookupRenderedItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectRenderedLookupItem(item);
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.isTypeaheadKey(event)) {
      event.preventDefault();
      this.handleTypeahead(event.key);
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen) {
          this.openDropdown();
        } else {
          this.moveActiveOption(event.key === 'ArrowDown' ? 1 : -1);
        }
        break;
      case 'Home':
        if (this.isOpen) {
          event.preventDefault();
          this.activeOptionIndex = 0;
          this.updateRenderedItemStates();
        }
        break;
      case 'End':
        if (this.isOpen && this.renderedLookupItems.length > 0) {
          event.preventDefault();
          this.activeOptionIndex = this.renderedLookupItems.length - 1;
          this.syncRenderedLookupItems();
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.isOpen) {
          this.openDropdown();
        } else {
          this.selectActiveOption();
        }
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.closeDropdown();
        }
        break;
      case 'Tab':
        this.closeDropdown();
        break;
    }
  }

  onFilterInput(): void {
    this.activeOptionIndex = this.getFilteredLookupItems().length > 0 ? 0 : -1;
    this.syncRenderedLookupItems();
  }

  onFilterKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.moveActiveOption(event.key === 'ArrowDown' ? 1 : -1);
        break;
      case 'Enter':
        event.preventDefault();
        this.selectActiveOption();
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
      case 'Tab':
        this.closeDropdown();
        break;
    }
  }

  onOptionMouseEnter(index: number): void {
    this.activeOptionIndex = index;
  }

  trackByRenderedLookupItem(index: number, item: AviatorLookupRenderedItem): string | number {
    return item.key;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }

    const target = event.target as HTMLElement | null;

    if (!target || !this.elementRef.nativeElement.contains(target)) {
      this.closeDropdown();
    }
  }

  protected lookupLoadComplete() {
    this.panelId = `${this.inputId || this.name || 'aoLookup'}List`;
    this.syncRenderedLookupItems();
    super.lookupLoadComplete();
  }

  setTranslatedValue(newValue) {
    super.setTranslatedValue(newValue);
    this.syncRenderedLookupItems();
  }

  private get lookupItemsForRender(): DwLookupInfo[] {
    const lookupItems = Array.isArray(this.lookupItems) && this.lookupItems.length
      ? this.lookupItems
      : (Array.isArray(this.allLookupItems) ? this.allLookupItems : []);
    return lookupItems;
  }

  private openDropdown(): void {
    if (AviatorLookupDropdownComponent.openDropdownInstance
      && AviatorLookupDropdownComponent.openDropdownInstance !== this) {
      AviatorLookupDropdownComponent.openDropdownInstance.closeDropdown();
    }

    this.isOpen = true;
    this.filterText = '';
    this.activeOptionIndex = this.getSelectedOptionIndex();
    AviatorLookupDropdownComponent.openDropdownInstance = this;
    this.updateRenderedItemStates();
    this.cdr.detectChanges();
  }

  private closeDropdown(): void {
    this.isOpen = false;
    this.filterText = '';
    this.activeOptionIndex = -1;
    this.updateRenderedItemStates();

    if (AviatorLookupDropdownComponent.openDropdownInstance === this) {
      AviatorLookupDropdownComponent.openDropdownInstance = null;
    }

    this.cdr.detectChanges();
  }

  private moveActiveOption(delta: number): void {
    const optionCount = this.renderedLookupItems.length;

    if (!optionCount) {
      this.activeOptionIndex = -1;
      this.updateRenderedItemStates();
      return;
    }

    if (this.activeOptionIndex < 0) {
      this.activeOptionIndex = delta > 0 ? 0 : optionCount - 1;
      this.updateRenderedItemStates();
      return;
    }

    this.activeOptionIndex = Math.min(optionCount - 1, Math.max(0, this.activeOptionIndex + delta));
    this.updateRenderedItemStates();
  }

  private selectActiveOption(): void {
    const activeRenderedItem = this.renderedLookupItems[this.activeOptionIndex];

    if (activeRenderedItem) {
      this.selectRenderedLookupItem(activeRenderedItem);
    }
  }

  private handleTypeahead(searchText: string): void {
    const normalizedSearchText = this.normalizeText(searchText);

    if (!normalizedSearchText) {
      return;
    }

    const matchingLookupItem = this.lookupItemsForRender.find(lookupItem =>
      this.normalizeText(this.getLookupItemDisplay(lookupItem)).startsWith(normalizedSearchText));

    if (!matchingLookupItem) {
      return;
    }

    if (this.isOpen) {
      const matchingIndex = this.renderedLookupItems.findIndex(item =>
        !item.isClear && this.areLookupValuesEqual(item.value, matchingLookupItem[this.valueAttrName]));

      if (matchingIndex >= 0) {
        this.activeOptionIndex = matchingIndex;
        this.updateRenderedItemStates();
      }

      return;
    }

    this.setTranslatedValue(matchingLookupItem[this.valueAttrName]);
  }

  private getSelectedOptionIndex(): number {
    if (this.translatedValue == null) {
      const filteredLookupItems = this.getFilteredLookupItems();
      return filteredLookupItems.length > 0 ? 0 : -1;
    }

    const filteredLookupItems = this.getFilteredLookupItems();
    const selectedIndex = filteredLookupItems.findIndex(lookupItem =>
      this.areLookupValuesEqual(lookupItem && lookupItem[this.valueAttrName], this.translatedValue));

    if (selectedIndex < 0) {
      return filteredLookupItems.length > 0 ? 0 : -1;
    }

    return this.showBlankOption ? selectedIndex + 1 : selectedIndex;
  }

  private getLookupItemByValue(value: any): DwLookupInfo | null {
    if (value == null) {
      return null;
    }

    return this.lookupItemsForRender.find(lookupItem =>
      this.areLookupValuesEqual(lookupItem && lookupItem[this.valueAttrName], value)) || null;
  }

  private normalizeText(value?: string | null): string {
    return (value || '').trim().toLowerCase();
  }

  private isTypeaheadKey(event: KeyboardEvent): boolean {
    return !event.altKey
      && !event.ctrlKey
      && !event.metaKey
      && event.key.length === 1
      && /\S/.test(event.key);
  }

  private getFilteredLookupItems(): DwLookupInfo[] {
    const normalizedFilter = this.normalizeText(this.filterText);

    if (!normalizedFilter) {
      return this.lookupItemsForRender;
    }

    return this.lookupItemsForRender.filter(lookupItem =>
      this.normalizeText(this.getLookupItemDisplay(lookupItem)).indexOf(normalizedFilter) >= 0);
  }

  private syncRenderedLookupItems(): void {
    const filteredLookupItems = this.getFilteredLookupItems();

    this.renderedLookupItems = filteredLookupItems.map((lookupItem, index) => ({
      isClear: false,
      display: this.getLookupItemDisplay(lookupItem),
      isSelected: this.areLookupValuesEqual(lookupItem && lookupItem[this.valueAttrName], this.translatedValue),
      key: lookupItem?.Id == null ? `blank-${index}` : lookupItem.Id,
      lookupItem,
      optionId: `${this.inputId || this.name || 'aoLookup'}-option-${index}`,
      value: lookupItem ? lookupItem[this.valueAttrName] : null
    }));

    if (this.showBlankOption && this.translatedValue != null) {
      this.renderedLookupItems.unshift({
        isClear: true,
        display: this.clearOptionText,
        isSelected: false,
        key: this.clearSelectionKey,
        lookupItem: null,
        optionId: `${this.inputId || this.name || 'aoLookup'}-option-clear`,
        value: null
      });
    }

    this.updateRenderedItemStates();
  }

  private updateRenderedItemStates(): void {
    this.renderedLookupItems.forEach((item, index) => {
      item.isSelected = this.areLookupValuesEqual(item.value, this.translatedValue);
    });

    this.activeDescendantId = this.isOpen && this.activeOptionIndex >= 0 && this.renderedLookupItems[this.activeOptionIndex]
      ? this.renderedLookupItems[this.activeOptionIndex].optionId
      : null;
  }
}
